import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import AnimatedSection from '../components/AnimatedSection';
import { pricingPlans } from '../mockData';
import { trackButtonClick, trackPageView } from '../utils/analytics';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    trackPageView('/pricing', 'Pricing');
  }, []);

  const handleBillingToggle = (cycle) => {
    setBillingCycle(cycle);
    trackButtonClick(`billing_${cycle}`, 'pricing_toggle');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1F44] to-[#0056D2] py-20">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="fadeInUp">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Transparent & Flexible Pricing
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Choose the perfect plan for your business. No hidden fees, no surprises.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Billing Toggle */}
      <section className="py-12 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="scaleIn">
            <div className="flex items-center justify-center space-x-4">
              <span className={`text-lg font-medium ${billingCycle === 'monthly' ? 'text-[#0056D2]' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => handleBillingToggle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className="relative w-16 h-8 bg-[#0056D2] rounded-full transition-colors duration-300"
              >
                <div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 ${
                    billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-lg font-medium ${billingCycle === 'yearly' ? 'text-[#0056D2]' : 'text-gray-500'}`}>
                Yearly
                <span className="ml-2 text-sm bg-[#FF7A00] text-white px-2 py-1 rounded-full">Save 15%</span>
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 150}>
                <div
                  className={`group bg-white rounded-3xl p-8 shadow-xl transition-all duration-500 hover:-translate-y-3 hover:scale-105 relative overflow-hidden h-full flex flex-col border-2 ${
                    plan.highlighted
                      ? 'border-[#0056D2] shadow-2xl'
                      : 'border-transparent hover:border-[#0056D2] hover:shadow-2xl'
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-[#0056D2] to-[#0A1F44] text-white text-center py-2 font-semibold text-sm">
                      Most Popular
                    </div>
                  )}
                  <div className={plan.highlighted ? 'mt-8' : 'mt-0'}>
                    <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6">{plan.users}</p>
                    <div className="mb-8">
                      {billingCycle === 'monthly' ? (
                        <>
                          <span className="text-5xl font-bold text-[#0056D2]">{plan.price}</span>
                          <span className="text-gray-600 text-lg">/month</span>
                        </>
                      ) : (
                        <>
                          <span className="text-5xl font-bold text-[#0056D2]">
                            ₹{Math.round(parseInt(plan.price.replace(/[^\d]/g, '')) * 12 * 0.85).toLocaleString('en-IN')}
                          </span>
                          <span className="text-gray-600 text-lg">/year</span>
                          <div className="text-sm text-gray-500 mt-2">
                            <span className="line-through">₹{(parseInt(plan.price.replace(/[^\d]/g, '')) * 12).toLocaleString('en-IN')}</span>
                            <span className="ml-2 text-[#FF7A00] font-semibold">Save 15%</span>
                          </div>
                        </>
                      )}
                    </div>
                    <ul className="space-y-4 mb-8 flex-grow">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start space-x-3">
                          <CheckCircle2 size={20} className="text-[#0056D2] flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact">
                      <Button
                        onClick={() => trackButtonClick(`select_${plan.name.toLowerCase()}`, 'pricing_plans')}
                        className={`w-full font-semibold py-6 rounded-full transition-all duration-200 hover:scale-105 ${
                          plan.highlighted
                            ? 'bg-[#FF7A00] hover:bg-[#e66d00] text-white'
                            : 'bg-[#0056D2] hover:bg-[#0A1F44] text-white'
                        }`}
                      >
                        Get Started
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Time to Upgrade Section with b6 image */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="slideInLeft">
              <div className="space-y-6">
                <div className="inline-block bg-[#FF7A00]/10 text-[#FF7A00] px-4 py-2 rounded-full text-sm font-semibold">
                  Upgrade Today
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44]">
                  Time to Upgrade How You Work!
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Experience the Next Level of Business Performance with Cloud.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Zero Downtime, Seamless Operations</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Access Anytime, Anywhere</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="text-[#0056D2] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">Secure Cloud Access for Tally</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="slideInRight">
              <img
                src="https://tally-bharat.s3.ap-south-1.amazonaws.com/media/tb-media-b6.jpeg"
                alt="Time to Upgrade How You Work"
                className="w-full h-auto rounded-3xl shadow-2xl"
                style={{ maxWidth: '1200px', maxHeight: '1200px' }}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] mb-4">
                Why Choose TallyBharat Cloud?
              </h2>
              <p className="text-xl text-gray-600">
                Enterprise-grade features for businesses of all sizes
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              'Highly secure solution with safe data storage',
              'Single-click login via portal',
              'Admin controls: add, shift, delete users, check logs, force logout',
              'Self-data upload & easy download',
              'Schedule & restore backups securely',
              'Live chat support',
              '24/7 access from anywhere',
              'Multiple backups & no hardware cost',
              'TwoFA & IP restriction',
              'Day & time management options'
            ].map((feature, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 50}>
                <div className="group bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-l-4 border-[#0056D2]">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#0056D2] to-[#0A1F44] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle2 size={14} className="text-white" />
                    </div>
                    <span className="text-gray-800 font-medium leading-relaxed">{feature}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { text: 'Get benefits on higher plans', highlight: false },
              { text: 'Managed Services available', highlight: false }
            ].map((item, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 100}>
                <div className="rounded-2xl p-6 text-center shadow-md bg-gradient-to-br from-[#F5F7FA] to-white text-[#0A1F44] border border-gray-200">
                  <p className="text-lg font-semibold">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-20 bg-gradient-to-b from-[#F5F7FA] to-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <div className="bg-gradient-to-r from-[#0056D2] to-[#0A1F44] rounded-3xl p-12 text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Need an Enterprise Plan?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Custom plans available for partners, enterprises, and organizations with specific requirements.
              </p>
              <Link to="/contact">
                <Button
                  onClick={() => trackButtonClick('contact_enterprise', 'pricing_enterprise')}
                  className="bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold px-10 py-6 text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-2xl"
                >
                  Contact Sales
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fadeInUp">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1F44] text-center mb-12">
              Frequently Asked Questions
            </h2>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: 'Can I upgrade or downgrade my plan?',
                a: 'Yes, you can change your plan at any time. Upgrades take effect immediately, and downgrades will be applied at the start of your next billing cycle.'
              },
              {
                q: 'Is there a setup fee?',
                a: 'No, there are no setup fees. You only pay the subscription price, and we handle all the setup and configuration for you.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards, debit cards, net banking, UPI, and bank transfers for annual plans.'
              },
              {
                q: 'Do you offer refunds?',
                a: 'Yes, we offer a 7-day money-back guarantee. If you\'re not satisfied, we\'ll refund your payment in full. See our Return Policy for details.'
              }
            ].map((faq, index) => (
              <AnimatedSection key={index} animation="fadeInUp" delay={index * 100}>
                <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold text-[#0A1F44] mb-3">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;